import { useEffect, type RefObject } from "react";
import { savePlaybackSpeed } from "@/lib/playbackMemory";

/**
 * Keyboard shortcuts for the PUBLIC game-view video player.
 *
 *   Space / K      play ⇄ pause
 *   J / L          ±10 s
 *   ←/→            ±10 s (shift → ±30 s)
 *   M              toggle mute
 *   F              fullscreen toggle
 *   0 / Home       jump to start
 *   End            jump to end
 *   <  /  >        playback rate −0.25 / +0.25 (clamped [0.25, 4.0])
 *
 * SEPARATE from M5's `useVideoKeyboardShortcuts` (which gates on
 * Coach Review's body attribute + only handles the playback subset
 * that overlaps with the tool-arming hook). The public game view
 * has no surface conflicts, so this hook does NOT gate on a body
 * attribute — only on:
 *   - The `<video>` element being mounted (no-op until ref attaches)
 *   - Cmd/Ctrl/Alt held → skip (browser shortcuts win)
 *   - Input focus → skip so typing in a future search box can't
 *     scrub the video
 *
 * Saves playback rate to `tktic_playback_speed` localStorage via
 * `savePlaybackSpeed` on `<` / `>` so the preference persists.
 */

const SEEK_SMALL = 10;
const SEEK_LARGE = 30;
const RATE_STEP = 0.25;
const RATE_MIN = 0.25;
const RATE_MAX = 4.0;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function usePublicVideoShortcuts({
  videoRef,
  containerRef,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Element to enter fullscreen on F press — defaults to the
   * video itself if not provided. The game shell passes the
   * video's wrapper so the toolbar / score overlay stay in
   * fullscreen too. */
  containerRef?: RefObject<HTMLElement | null>;
}): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const video = videoRef.current;
      if (!video) return;
      // Mostly destructive shortcuts — preventDefault so space
      // doesn't scroll the page, arrow keys don't scroll, etc.
      switch (e.key) {
        case " ":
        case "k":
        case "K":
          if (video.paused) void video.play();
          else video.pause();
          e.preventDefault();
          return;
        case "j":
        case "J":
          video.currentTime = Math.max(0, video.currentTime - SEEK_SMALL);
          e.preventDefault();
          return;
        case "l":
        case "L":
          video.currentTime = Math.min(
            isFinite(video.duration) ? video.duration : Number.MAX_SAFE_INTEGER,
            video.currentTime + SEEK_SMALL,
          );
          e.preventDefault();
          return;
        case "ArrowLeft":
          video.currentTime = Math.max(
            0,
            video.currentTime - (e.shiftKey ? SEEK_LARGE : SEEK_SMALL),
          );
          e.preventDefault();
          return;
        case "ArrowRight":
          video.currentTime = Math.min(
            isFinite(video.duration) ? video.duration : Number.MAX_SAFE_INTEGER,
            video.currentTime + (e.shiftKey ? SEEK_LARGE : SEEK_SMALL),
          );
          e.preventDefault();
          return;
        case "m":
        case "M":
          video.muted = !video.muted;
          e.preventDefault();
          return;
        case "f":
        case "F": {
          const target = containerRef?.current ?? video;
          if (document.fullscreenElement) {
            void document.exitFullscreen();
          } else {
            void target.requestFullscreen?.();
          }
          e.preventDefault();
          return;
        }
        case "0":
        case "Home":
          video.currentTime = 0;
          e.preventDefault();
          return;
        case "End":
          if (isFinite(video.duration)) {
            video.currentTime = video.duration;
          }
          e.preventDefault();
          return;
        case "<":
        case ",": {
          const next = clampRate(video.playbackRate - RATE_STEP);
          video.playbackRate = next;
          savePlaybackSpeed(next);
          e.preventDefault();
          return;
        }
        case ">":
        case ".": {
          const next = clampRate(video.playbackRate + RATE_STEP);
          video.playbackRate = next;
          savePlaybackSpeed(next);
          e.preventDefault();
          return;
        }
        default:
          return;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [videoRef, containerRef]);
}

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1.0;
  if (rate < RATE_MIN) return RATE_MIN;
  if (rate > RATE_MAX) return RATE_MAX;
  // Round to 2 decimal places to keep the rate-display readable
  // and avoid floating-point creep across many key presses.
  return Math.round(rate * 100) / 100;
}
